import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import PageLayout from "@/components/feature/PageLayout";
import SectionTitle from "@/components/base/SectionTitle";
import CountUp from "@/components/base/CountUp";
import { newsItems as newsData } from "@/mocks/news";

function useAnimateOnScroll() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
    );
    el.querySelectorAll(".reveal").forEach((child) => obs.observe(child));
    return () => obs.disconnect();
  }, []);
  return ref;
}

const trustStats = [
  { value: "49", unit: "年", label: "創業以来の実績", sub: "1977年創業" },
  { value: "ISO", unit: "9001", label: "品質マネジメント認証", sub: "NQA・UKAS認定" },
  { value: "50", unit: "台", label: "ガス腐食試験機保有台数", sub: "国内最大級の設備台数" },
  { value: "最短", unit: "即日", label: "スピード対応", sub: "サンプル到着後、即日試験スタート" },
];

const whyFactk = [
  {
    icon: "ri-microscope-line",
    title: "国内トップクラスの試験設備",
    desc: "自社開発のガス腐食試験機KGシリーズと接触抵抗評価装置MS7500を中心に、最先端の評価環境を完備。5種の腐食性ガスに対応した高精度試験が可能です。",
  },
  {
    icon: "ri-settings-3-line",
    title: "自社開発試験機",
    desc: "試験機メーカーとしての知見を活かし、自社で設計・製造した機器だからこそ実現できる、高度なカスタマイズと安定した性能。",
  },
  {
    icon: "ri-history-line",
    title: "創業以来約1万件の試験実績",
    desc: "培ったノウハウを活かし、試験実施から詳細な結果報告まで一貫して対応。ご要望に応じて、過去の類似試験事例に基づく知見も共有します。",
  },
  {
    icon: "ri-shield-check-line",
    title: "高い再現性と品質管理",
    desc: "ISO 9001認証に基づく厳格な品質管理体制のもと、試験の再現性とデータの信頼性を徹底。グローバル規格に準拠した評価を提供します。",
  },
];

const services = [
  {
    title: "ガス腐食試験",
    features: [
      "5種の腐食性ガス（H₂S、SO₂、NO₂、Cl₂、O₃）",
      "単独・混合ガス試験に対応",
      "ISO、IEC、JIS、ASTM等の国際規格に準拠",
    ],
    image: "https://public.readdy.ai/ai/img_res/edited_a8640d71823eec9bdfdbfa1bab0f51f5_08d470ed.jpg",
    link: "/service/test-guide#gas-corrosion",
  },
  {
    title: "接触抵抗評価分析",
    features: [
      "接触抵抗と接触力の高精度測定",
      "µΩレベルの微小抵抗評価",
      "板材・丸棒等ほとんどの接点材料に対応",
    ],
    image: "https://storage.readdy-site.link/project_files/46c769b0-62ed-4752-96b8-d59840c4cf62/c5b3bb46-d31b-44b0-965a-8fc8b03761e8_compressed_Untitled-design-3-1.webp",
    link: "/service/test-guide#contact-resistance",
  },
  {
    title: "その他受託試験",
    features: [
      "カスタム試験条件のご相談",
      "高温放置・耐湿・振動・塩水噴霧",
      "外部試験機関との連携対応",
    ],
    image: "https://storage.readdy-site.link/project_files/46c769b0-62ed-4752-96b8-d59840c4cf62/a0036a0f-cecf-4141-bc31-668c54167c68_compressed_IMG_5493-Copy.webp",
    link: "/service/test-guide#other-tests",
  },
];

const products = [
  {
    name: "ガス腐食試験機",
    category: "試験機器",
    link: "/products#kg200",
    image: "https://storage.readdy-site.link/project_files/46c769b0-62ed-4752-96b8-d59840c4cf62/774725c2-be14-45fd-a593-2aace2beeb1f_compressed_IMG_5485.webp",
    features: ["大型チャンバー", "JIS/IEC対応", "高精度ガス制御"],
  },
  {
    name: "接触抵抗評価システム",
    category: "測定機器",
    link: "/products#ms7500",
    image: "https://storage.readdy-site.link/project_files/46c769b0-62ed-4752-96b8-d59840c4cf62/f0ea9a7f-d24c-488e-8508-8d91430d10b0_compressed_IMG_5472-3.webp",
    features: ["μΩレベル測定", "高速多点測定", "高い再現性"],
  },
  {
    name: "表面処理剤シリーズ",
    category: "化学品",
    link: "/products#dryalfa",
    image: "https://storage.readdy-site.link/project_files/46c769b0-62ed-4752-96b8-d59840c4cf62/a8b54d4e-59b0-4c38-889d-70a915b54a27_compressed_IMG_5491-Copy.webp",
    features: ["防錆・酸化防止", "低挿抜力", "長寿命化"],
  },
];

const newsItems = newsData.filter((_, i) => i < 5);

export default function Home() {
  const [currentBg, setCurrentBg] = useState(0);
  const [showFloating, setShowFloating] = useState(false);
  const bgImages = [
    "https://storage.readdy-site.link/project_files/46c769b0-62ed-4752-96b8-d59840c4cf62/b9cd063c-ad14-45e3-9443-9ef06ccc9a11_compressed_IMG_5507-Copy.webp",
    "https://storage.readdy-site.link/project_files/46c769b0-62ed-4752-96b8-d59840c4cf62/c5b3bb46-d31b-44b0-965a-8fc8b03761e8_compressed_Untitled-design-3-1.webp",
    "https://storage.readdy-site.link/project_files/46c769b0-62ed-4752-96b8-d59840c4cf62/de1a2cab-5581-465e-873b-fe1e1ed2443d_compressed_NQA_ISO9001_CMYK_UKAS.webp",
    "https://storage.readdy-site.link/project_files/46c769b0-62ed-4752-96b8-d59840c4cf62/a8b54d4e-59b0-4c38-889d-70a915b54a27_compressed_IMG_5491-Copy.webp",
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBg((prev) => (prev + 1) % bgImages.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowFloating(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const trustRef = useAnimateOnScroll();
  const newsRef = useAnimateOnScroll();
  const whyRef = useAnimateOnScroll();
  const servicesRef = useAnimateOnScroll();
  const productsRef = useAnimateOnScroll();
  const aboutRef = useAnimateOnScroll();

  return (
    <PageLayout>
      {/* ───────────────── Hero ───────────────── */}
      <section className="relative min-h-[620px] md:min-h-[760px] flex items-center justify-center overflow-hidden bg-primary-700">
        {bgImages.map((src, i) => (
          <img
            key={src}
            src={src}
            alt=""
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[2000ms] ease-in-out ${
              i === currentBg ? "opacity-40" : "opacity-0"
            }`}
          />
        ))}
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative z-10 w-full px-4 md:px-8 text-center">
          <div className="max-w-3xl mx-auto">
            <p className="text-background-200/90 text-xs md:text-sm font-medium tracking-[0.25em] uppercase mb-5 font-label">
              FACT K Co., Ltd.
            </p>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-black text-background-50 leading-[1.15] mb-6 font-heading">
              地球上より接触不良を
              <br />
              無くしたい
            </h1>
            <p className="text-background-200/80 text-sm md:text-lg leading-relaxed mb-9 max-w-xl mx-auto font-body">
              ガス腐食試験・接触抵抗評価・表面処理技術で、
              <br />
              電子部品の信頼性を支える専門企業
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/service"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-background-50 text-foreground-950 font-semibold text-sm hover:bg-background-100 transition-all duration-300 whitespace-nowrap font-label"
              >
                <span>サービスを見る</span>
                <i className="ri-arrow-right-line" />
              </Link>
              <Link
                to="/company"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-background-400/40 text-background-50 font-semibold text-sm hover:bg-background-50/10 transition-all duration-300 whitespace-nowrap font-label"
              >
                <span>会社案内</span>
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-background-300/60">
          <span className="text-[10px] tracking-[0.2em] uppercase font-label">Scroll</span>
          <div className="w-[1px] h-8 bg-gradient-to-b from-background-300/40 to-transparent" />
        </div>

        {/* Free Trial Floating Banner */}
        <div className="absolute bottom-0 left-0 right-0 z-10">
          <div className="w-full px-4 md:px-8 pb-4 md:pb-6">
            <div className="max-w-2xl mx-auto">
              <Link
                to="/service/free-trial"
                className="block relative rounded-xl overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-primary-600 via-primary-500 to-accent-500" />
                <div className="relative z-10 flex items-center justify-between gap-4 px-4 py-3.5 sm:px-5 sm:py-4 md:px-6 md:py-5">
                  <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg bg-white/20 backdrop-blur-sm border border-white/30 group-hover:bg-white/30 transition-all">
                      <i className="ri-gift-line text-lg sm:text-xl text-white" />
                    </div>
                    <div className="text-left">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="px-2 py-0.5 rounded-full bg-white/20 border border-white/30 text-white text-[10px] sm:text-xs font-bold whitespace-nowrap">
                          近日開始予定
                        </span>
                      </div>
                      <p className="text-white font-bold text-xs sm:text-sm md:text-base leading-tight whitespace-nowrap">
                        ガス腐食試験 無料体験
                      </p>
                    </div>
                  </div>
                  <div className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full bg-white text-primary-600 font-bold text-xs sm:text-sm group-hover:bg-background-50 transition-colors whitespace-nowrap">
                    <span>詳しく見る</span>
                    <i className="ri-arrow-right-line" />
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────── Trust Stats ───────────────── */}
      <section className="py-16 md:py-24 bg-background-50 border-b border-background-200/30" ref={trustRef}>
        <div className="w-full px-4 md:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {trustStats.map((stat, i) => (
                <div
                  key={i}
                  className="reveal text-center group"
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div className="flex items-baseline justify-center gap-0.5">
                    {!isNaN(Number(stat.value)) ? (
                      <CountUp
                        end={Number(stat.value)}
                        className="text-4xl md:text-5xl lg:text-6xl font-black text-foreground-950 font-heading tracking-tight"
                      />
                    ) : (
                      <span className="text-4xl md:text-5xl lg:text-6xl font-black text-foreground-950 font-heading tracking-tight">
                        {stat.value}
                      </span>
                    )}
                    <span className="text-xl md:text-2xl font-bold text-primary-500 font-heading">
                      {stat.unit}
                    </span>
                  </div>
                  <p className="mt-2 text-sm font-bold text-foreground-900 font-heading">
                    {stat.label}
                  </p>
                  <p className="mt-1 text-xs text-foreground-500">
                    {stat.sub}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="h-px bg-background-200/60" />

      {/* ───────────────── News ───────────────── */}
      <section className="py-16 md:py-20 bg-background-50" ref={newsRef}>
        <div className="w-full px-4 md:px-8">
          <SectionTitle
            title="お知らせ"
            subtitle="試験結果報告書や規格資料など、最新情報をお届けしています"
            centered
            label="News"
          />
          <div className="max-w-4xl mx-auto mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {newsItems.map((item, i) => {
                const isExternal = !!item.href;
                const linkProps = isExternal
                  ? { href: item.href, target: "_blank", rel: "noopener noreferrer" }
                  : { to: item.to };
                const Wrapper = isExternal ? "a" : Link;
                return (
                  <Wrapper
                    key={i}
                    {...linkProps}
                    className="reveal card-lift flex items-center gap-3 px-5 py-4 rounded-xl bg-background-50 border border-background-200/40 hover:border-primary-300 transition-all duration-200 group"
                    style={{ transitionDelay: `${i * 80}ms` }}
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 shrink-0 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                      <i className={`${isExternal ? "ri-file-pdf-line" : "ri-article-line"} text-lg`} />
                    </div>
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="text-sm text-foreground-700 group-hover:text-foreground-900 transition-colors line-clamp-2 leading-relaxed font-medium">
                        {item.label}
                      </span>
                      {item.isNew && (
                        <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded bg-primary-500 text-background-50 text-[10px] font-bold">
                          NEW
                        </span>
                      )}
                    </div>
                    <i className={`${isExternal ? "ri-arrow-right-up-line" : "ri-arrow-right-line"} text-foreground-400 text-xs ml-auto shrink-0 group-hover:text-primary-500 transition-colors`} />
                  </Wrapper>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────── Why FactK ───────────────── */}
      <section className="py-20 md:py-28 bg-background-100" ref={whyRef}>
        <div className="w-full px-4 md:px-8">
          <SectionTitle
            title={
              <>
                <span className="tracking-wide">FactK</span>
                {" が選ばれる理由"}
              </>
            }
            subtitle="お客様の品質と信頼性を支える、4つの強み"
            centered
            label="Why Choose Us"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 mt-4">
            {whyFactk.map((item, i) => (
              <div
                key={i}
                className="reveal card-lift bg-background-50 rounded-xl p-6 md:p-7 border border-background-200/40 group"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 mb-5 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                  <i className={`${item.icon} text-xl`} />
                </div>
                <h3 className="text-base font-bold text-foreground-950 mb-2 font-heading">
                  {item.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────── Services ───────────────── */}
      <section className="py-20 md:py-28 bg-background-50" ref={servicesRef}>
        <div className="w-full px-4 md:px-8">
          <SectionTitle
            title="サービス"
            subtitle="接触不良の原因究明から対策提案まで、一貫したソリューション"
            centered
            label="Our Services"
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10 mt-4">
            {services.map((s, i) => (
              <Link
                key={i}
                to={s.link}
                className="reveal card-lift group bg-background-50 border border-background-200/40 rounded-xl overflow-hidden block"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-full object-cover group-hover:scale-[1.03] img-zoom-smooth"
                  />
                </div>
                <div className="p-6 md:p-7">
                  <h3 className="text-lg md:text-xl font-bold text-foreground-950 mb-3 font-heading">
                    {s.title}
                  </h3>
                  <ul className="space-y-2 mb-4">
                    {s.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-foreground-600">
                        <i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0 text-sm" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 group-hover:text-primary-700 transition-colors font-label">
                    <span>詳しく見る</span>
                    <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-0.5" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/service"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap font-label"
            >
              <span>すべてのサービスを見る</span>
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* ───────────────── Products ───────────────── */}
      <section className="py-20 md:py-28 bg-background-100" ref={productsRef}>
        <div className="w-full px-4 md:px-8">
          <SectionTitle
            title="製品"
            subtitle="自社開発の試験機器から表面処理剤まで、高品質なラインナップ"
            centered
            label="Our Products"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10 mt-4">
            {products.map((p, i) => (
              <Link
                key={i}
                to={p.link}
                className="reveal card-lift group bg-background-50 rounded-xl overflow-hidden border border-background-200/40 block"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="aspect-[3/2] overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-full h-full object-cover group-hover:scale-[1.03] img-zoom-smooth"
                  />
                </div>
                <div className="p-5 md:p-6">
                  <span className="inline-block px-2.5 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-3 font-label">
                    {p.category}
                  </span>
                  <h3 className="text-base font-bold text-foreground-950 mb-3 font-heading">
                    {p.name}
                  </h3>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {p.features.map((f, j) => (
                      <span key={j} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-background-100 text-foreground-700 text-xs">
                        <i className="ri-check-line text-primary-500 text-xs" />
                        {f}
                      </span>
                    ))}
                  </div>
                  <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 group-hover:text-primary-700 transition-colors font-label">
                    <span>詳しく見る</span>
                    <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-0.5" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/products"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap font-label"
            >
              <span>すべての製品を見る</span>
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* ───────────────── About / Company ───────────────── */}
      <section className="py-20 md:py-28 bg-background-50" ref={aboutRef}>
        <div className="w-full px-4 md:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14 items-center">
              <div className="reveal lg:col-span-3">
                <div className="aspect-[8/5] rounded-xl overflow-hidden">
                  <img
                    src="https://public.readdy.ai/ai/img_res/edited_54038e647bb4ef73d471a80d37d19407_fa54f14c.jpg"
                    alt="ファクトケイ株式会社"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="reveal lg:col-span-2" style={{ transitionDelay: "100ms" }}>
                <SectionTitle
                  title="ごあいさつ"
                  subtitle="昭和52年の創業以来、「地球上より接触不良を無くしたい」という理念のもと、接触機構部品のガス腐食試験・接触抵抗評価分析・メタルコンタクト表面処理液の専門企業として歩んでまいりました。"
                />
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  お客様の製品信頼性向上に貢献することが私たちの使命です。今後も技術の研鑽を重ね、より高度な評価・分析サービスを提供してまいります。
                </p>
                <Link
                  to="/company"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors font-label group"
                >
                  <span>会社案内を見る</span>
                  <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-0.5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────── Recruit ───────────────── */}
      <section className="py-16 md:py-24 bg-background-100">
        <div className="w-full px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-14">
              <div className="flex-shrink-0">
                <div className="relative">
                  <div className="w-28 h-28 md:w-36 md:h-36 rounded-2xl bg-accent-500 flex items-center justify-center">
                    <i className="ri-user-search-line text-3xl md:text-4xl text-background-50" />
                  </div>
                  <span className="absolute -top-1.5 -right-1.5 inline-flex items-center px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-bold whitespace-nowrap font-label">
                    募集中
                  </span>
                </div>
              </div>
              <div className="flex-1 text-center lg:text-left">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3 font-heading">採用情報</h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4 max-w-lg">
                  ガス腐食試験に使用する装置の点検や動作確認などの検査アシスタント業務。
                  未経験の方でも先輩スタッフが丁寧に指導します。正社員・パートいずれも募集中です。
                </p>
                <div className="flex flex-wrap gap-2 mb-5 justify-center lg:justify-start">
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-accent-100 text-accent-800 text-xs font-medium">
                    <i className="ri-map-pin-line text-xs" />
                    東京勤務
                  </span>
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-accent-100 text-accent-800 text-xs font-medium">
                    <i className="ri-user-smile-line text-xs" />
                    未経験歓迎
                  </span>
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-accent-100 text-accent-800 text-xs font-medium">
                    <i className="ri-briefcase-line text-xs" />
                    正社員／パート可
                  </span>
                </div>
                <Link
                  to="/recruit"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-accent-500 text-background-50 font-semibold text-sm hover:bg-accent-600 transition-all duration-300 whitespace-nowrap font-label"
                >
                  <span>採用情報を見る</span>
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────── Mobile floating recruit button ───────────────── */}
      <Link
        to="/recruit"
        className={`fixed bottom-6 right-6 z-50 inline-flex items-center gap-1.5 px-5 py-3 rounded-full bg-accent-500 text-background-50 text-xs md:text-sm font-bold hover:bg-accent-600 active:scale-95 whitespace-nowrap font-label transition-all duration-500 ${
          showFloating ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
        }`}
      >
        <i className="ri-user-search-line" />
        <span>採用情報はこちら</span>
        <i className="ri-arrow-right-s-line" />
      </Link>

      {/* ───────────────── Floating free trial button ───────────────── */}
      <Link
        to="/service/free-trial"
        className={`fixed bottom-20 right-6 z-50 inline-flex items-center gap-1.5 px-5 py-3 rounded-full bg-primary-500 text-background-50 text-xs md:text-sm font-bold hover:bg-primary-600 active:scale-95 whitespace-nowrap font-label transition-all duration-500 ${
          showFloating ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
        }`}
      >
        <i className="ri-gift-line" />
        <span>無料体験 近日開始</span>
        <i className="ri-arrow-right-s-line" />
      </Link>

      {/* ───────────────── CTA ───────────────── */}
      <section className="py-20 md:py-32 bg-primary-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20minimalist%20technology%20background%20with%20subtle%20geometric%20grid%20pattern%20and%20soft%20light%20gradients%2C%20dark%20sophisticated%20tone%2C%20clean%20corporate%20atmosphere%2C%20premium%20engineering%20aesthetic%2C%20subtle%20metallic%20accents%2C%20very%20dark%20navy%20base%20with%20barely%20visible%20technical%20pattern%2C%20elegant%20and%20restrained&width=1600&height=600&seq=factk-cta-bg&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 w-full px-4 md:px-8 text-center">
          <h2 className="text-2xl md:text-4xl font-bold text-background-50 mb-4 font-heading">
            まずは、お気軽にご相談ください
          </h2>
          <p className="text-background-200/80 text-sm md:text-base max-w-lg mx-auto mb-10 leading-relaxed">
            接触不良のお悩みや試験・分析のご依頼、製品に関するご質問など、
            専門スタッフが丁寧に対応いたします。
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-background-50 text-foreground-950 font-bold text-sm hover:bg-background-100 transition-all duration-300 whitespace-nowrap font-label"
          >
            <span>お問い合わせ</span>
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>
    </PageLayout>
  );
}